(function () {
  // 1) CSS override so the poster shows when paused by user
  const style = document.createElement('style');
  style.textContent = `
    .deferred-media[loaded][data-paused="true"] > .deferred-media__poster {
      display: block !important;
    }
  `;
  document.head.appendChild(style);

  // 2) Helpers
  function pickLiveVideo(container) {
    // Return the real video (exclude any within <template>)
    const vids = Array.from(container.querySelectorAll('video'));
    return vids.find((v) => v.closest('template') === null) || null;
  }

  function getPoster(container) {
    return container.querySelector('.deferred-media__poster');
  }

  function showPoster(container, show) {
    if (show) {
      container.setAttribute('data-paused', 'true');
    } else {
      container.removeAttribute('data-paused');
    }
    const poster = getPoster(container);
    if (poster) {
      poster.style.display = show ? 'block' : '';
    }
  }

  function isInViewport(el) {
    const r = el.getBoundingClientRect();
    const vw = window.innerWidth || document.documentElement.clientWidth;
    const vh = window.innerHeight || document.documentElement.clientHeight;
    return r.bottom > 0 && r.right > 0 && r.top < vh && r.left < vw;
  }

  // 3) IO that manages pause when the video leaves the viewport, and play on enter (unless user paused)
  const videoIO = new IntersectionObserver(
    (entries) => {
      for (const entry of entries) {
        const video = entry.target;
        const container = video.closest('.deferred-media');
        if (!container) continue;

        const userPaused = container.__userPaused === true;

        if (entry.isIntersecting) {
          if (!userPaused) {
            video.play().catch(() => {
              // Autoplay might be blocked; ignore
            });
          }
        } else {
          try {
            video.pause();
          } catch (e) {}
        }
      }
    },
    { root: null, rootMargin: '0px', threshold: 0.01 }
  );

  // 4) IO that triggers loading the media when the container enters the viewport
  const containerIO = new IntersectionObserver(
    (entries) => {
      for (const entry of entries) {
        const container = entry.target;
        if (!entry.isIntersecting) continue;

        // If not loaded yet, simulate click on the poster to let the theme inject the real video
        const isLoaded = container.hasAttribute('loaded');
        if (!isLoaded) {
          const poster = getPoster(container);
          if (poster) {
            // Simulate user click to trigger native theme behavior
            poster.click();
          }
          // We’ll wait for MutationObserver to see the video and wire it
          continue;
        }

        // If loaded and live video exists, ensure it’s wired and try to play
        const liveVideo = pickLiveVideo(container);
        if (liveVideo) {
          wireVideo(container, liveVideo);
          if (!container.__userPaused) {
            liveVideo.play().catch(() => {});
          }
        }
      }
    },
    { root: null, rootMargin: '0px', threshold: 0.01 }
  );

  // 5) Attach behavior to a live video
  function wireVideo(container, video) {
    if (!container || !video) return;
    if (video.__deferredBound) return;

    // Ensure inline autoplay compatibility
    video.setAttribute('playsinline', '');
    video.setAttribute('muted', '');
    video.playsInline = true;
    video.muted = true;

    // Toggle handler (video or poster)
    const onToggle = (e) => {
      e.stopPropagation();
      // Mark that the user explicitly interacted
      container.__userPaused = true;

      if (video.paused) {
        showPoster(container, false);
        video.play().catch(() => {});
      } else {
        video.pause();
        showPoster(container, true);
      }
    };

    const poster = getPoster(container);
    if (poster && !poster.__deferredBound) {
      poster.addEventListener('click', onToggle);
      poster.__deferredBound = true;
    }
    if (!video.__toggleBound) {
      video.addEventListener('click', onToggle);
      video.__toggleBound = true;
    }

    video.addEventListener('ended', () => {
      showPoster(container, true);
    });

    // Observe video for viewport visibility (pause when it leaves)
    videoIO.observe(video);

    // If already visible, try to start (unless user paused)
    if (!container.__userPaused && isInViewport(video)) {
      video.play().catch(() => {});
    }

    video.__deferredBound = true;
  }

  // 6) Initialize one .deferred-media container
  function initContainer(container) {
    if (!container || container.__containerBound) {
      return;
    }
    container.__containerBound = true;

    // Observe the container so we can auto-load when it enters viewport
    containerIO.observe(container);

    // If a live video already exists (some themes may inject immediately), wire it now
    const liveVideo = pickLiveVideo(container);
    if (liveVideo) {
      wireVideo(container, liveVideo);
    }

    // Watch for when the theme marks it loaded and injects the video
    const mo = new MutationObserver((mutations) => {
      for (const m of mutations) {
        if (m.type === 'attributes' && m.attributeName === 'loaded') {
          // Loaded toggled; try to find the live video and wire it
          const v = pickLiveVideo(container);
          if (v) {
            wireVideo(container, v);
          }
        } else if (m.type === 'childList') {
          // If a live video node got added, wire it
          const added = Array.from(m.addedNodes || []);
          for (const node of added) {
            if (node.nodeType === 1) {
              const asEl = node;
              if (asEl.tagName && asEl.tagName.toLowerCase() === 'video' && asEl.closest('template') === null) {
                wireVideo(container, asEl);
              } else {
                const v = asEl.querySelector && asEl.querySelector('video');
                if (v && v.closest('template') === null) {
                  wireVideo(container, v);
                }
              }
            }
          }
        }
      }
    });

    mo.observe(container, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['loaded'],
    });
  }

  // 7) Bootstrapping for all current and future .deferred-media elements
  function initAll() {
    document.querySelectorAll('.deferred-media').forEach(initContainer);
  }

  const docObserver = new MutationObserver((mutations) => {
    for (const m of mutations) {
      if (m.type === 'childList') {
        m.addedNodes.forEach((node) => {
          if (!(node instanceof Element)) return;
          if (node.matches && node.matches('.deferred-media')) {
            initContainer(node);
          }
          node.querySelectorAll?.('.deferred-media').forEach(initContainer);
        });
      }
    }
  });

  function start() {
    initAll();
    docObserver.observe(document.documentElement, {
      childList: true,
      subtree: true,
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }
})();